import { useCallback, useRef, useState } from "react";
import {
  CountStatus,
  pollCountEstimate,
  startCountEstimate,
} from "../api/count";

// Filter-estimate state machine. Lives at the wizard level so the
// funnel breadcrumb and the Filter step's Estimate button read/write
// the same state.
//
// Behavior:
//  - Pure manual: estimates only kick when `kick()` is called. The
//    Filter step's "Estimate" button calls it; the wizard's Next
//    button on the Filter step also calls it before advancing.
//  - Auto-retries up to MAX_AUTO_RETRIES on transient failures
//    (RemoteProtocolError, etc.). Permanent errors (parse errors,
//    missing rollups) surface immediately.
//  - Tracks "estimatedFor" (filter + sorted months key) so the funnel
//    can show "(stale)" when the user has typed past the last estimate.

export interface EstimateKey {
  filter: string;
  monthsKey: string;
}

// Live progress info from the portal's per-month fan-out. Optional —
// older portals (or the first poll before the priming publish) don't
// include it.
export interface EstimateProgress {
  completed: number;
  total: number;
  partialTotal: number;
}

export type EstimateState =
  | { kind: "idle" }
  | { kind: "running"; for: EstimateKey; progress?: EstimateProgress }
  | {
      kind: "ok";
      total: number;
      byMonth: Record<string, number>;
      missingMonths: string[];
      for: EstimateKey;
    }
  | { kind: "error"; message: string; for: EstimateKey };

const POLL_INTERVAL_MS = 1000;
const MAX_AUTO_RETRIES = 2;
const PERMANENT_ERROR_HINTS = [
  "parse",
  "filter error",
  "missing rollup",
  "missing_months",
  "unknown column",
  "unknown function",
];

export function looksTransient(message: string | undefined): boolean {
  if (!message) return true;
  const lower = message.toLowerCase();
  return !PERMANENT_ERROR_HINTS.some((hint) => lower.includes(hint));
}

function monthsKeyOf(months: string[]): string {
  return [...months].sort().join(",");
}

function monthsToRange(
  months: string[],
): { start: string; end: string } | null {
  if (!months.length) return null;
  const sorted = [...months].sort();
  const [first, last] = [sorted[0], sorted[sorted.length - 1]];
  const start = `${first}-01`;
  const [ly, lm] = last.split("-").map((s) => parseInt(s, 10));
  const endY = lm === 12 ? ly + 1 : ly;
  const endM = lm === 12 ? 1 : lm + 1;
  const end = `${endY}-${String(endM).padStart(2, "0")}-01`;
  return { start, end };
}

// Minimal subset of InventoryRow that the empty-filter short-circuit
// reads. Kept narrow so the hook doesn't need to depend on the full
// api/inventory shape.
export interface InventoryRowLite {
  year: string;
  month: string;
  rollup_n_rows: number | null;
}

export interface FilterEstimate {
  state: EstimateState;
  // Manually trigger an estimate for the given filter + months.
  // When ``filter`` is empty and ``inventory`` is supplied, the count
  // is synthesised locally by summing rollup_n_rows for the selected
  // months — no operator round-trip, no S3 scan. Otherwise behaves
  // like a normal estimate kick.
  kick: (
    filter: string,
    months: string[],
    contentTypes: string[],
    inventory?: InventoryRowLite[],
  ) => void;
  // True iff there's a non-idle state but its `for` key doesn't match
  // the current filter+months. The funnel shows "(stale)" and the
  // Estimate button gets enabled.
  isStaleFor: (filter: string, months: string[]) => boolean;
}

export function useFilterEstimate(): FilterEstimate {
  const [state, setState] = useState<EstimateState>({ kind: "idle" });
  // Generation lets a fresh kick supersede an in-flight poll loop —
  // when the user clicks Estimate again before the previous result
  // lands, the old poll callbacks become no-ops.
  const generation = useRef(0);

  const kick = useCallback(
    (
      filter: string,
      months: string[],
      contentTypes: string[],
      inventory?: InventoryRowLite[],
    ) => {
      if (months.length === 0) {
        setState({ kind: "idle" });
        return;
      }
      if (!filter.trim()) {
        // Empty filter ⇒ "count every page in the selected months".
        // The inventory's rollup_n_rows is the ground truth for this
        // — built when each month's rollup landed, so it's already
        // accurate and instantly available client-side.
        if (!inventory) {
          setState({ kind: "idle" });
          return;
        }
        const byMonth: Record<string, number> = {};
        let total = 0;
        for (const m of months) {
          const [y, mm] = m.split("-");
          const row = inventory.find((r) => r.year === y && r.month === mm);
          const n = row?.rollup_n_rows ?? 0;
          byMonth[m] = n;
          total += n;
        }
        setState({
          kind: "ok",
          total,
          byMonth,
          missingMonths: [],
          for: { filter, monthsKey: monthsKeyOf(months) },
        });
        return;
      }
      const key: EstimateKey = { filter, monthsKey: monthsKeyOf(months) };
      const myGen = ++generation.current;
      setState({ kind: "running", for: key });

      const runAttempt = async (attempt: number) => {
        const range = monthsToRange(months);
        if (!range) return;
        let request_id: string;
        try {
          const r = await startCountEstimate({
            filter,
            start: range.start,
            end: range.end,
            content_types: contentTypes,
          });
          request_id = r.request_id;
        } catch (err) {
          if (myGen !== generation.current) return;
          if (attempt < MAX_AUTO_RETRIES) {
            setTimeout(() => runAttempt(attempt + 1), POLL_INTERVAL_MS);
            return;
          }
          setState({ kind: "error", message: String(err), for: key });
          return;
        }
        if (myGen !== generation.current) return;

        const poll = async () => {
          if (myGen !== generation.current) return;
          let status: CountStatus;
          try {
            status = await pollCountEstimate(request_id);
          } catch {
            // Transient HTTP blip — try the same id again next tick.
            setTimeout(poll, POLL_INTERVAL_MS);
            return;
          }
          if (myGen !== generation.current) return;
          if (status.status === "running") {
            // Progress fields are optional (older portal or first poll
            // before the priming publish). Surface them when present so
            // the funnel can render "Counted X of Y · Z so far".
            const progress: EstimateProgress | undefined =
              typeof status.progress_total === "number" &&
              typeof status.progress_completed === "number"
                ? {
                    completed: status.progress_completed,
                    total: status.progress_total,
                    partialTotal: status.partial_total ?? 0,
                  }
                : undefined;
            setState({ kind: "running", for: key, progress });
            setTimeout(poll, POLL_INTERVAL_MS);
            return;
          }
          if (status.status === "succeeded") {
            setState({
              kind: "ok",
              total: status.total,
              byMonth: status.by_month ?? {},
              missingMonths: status.missing_months ?? [],
              for: key,
            });
            return;
          }
          // status.failed — auto-retry transient errors only.
          if (attempt < MAX_AUTO_RETRIES && looksTransient(status.message)) {
            setTimeout(() => runAttempt(attempt + 1), POLL_INTERVAL_MS);
            return;
          }
          setState({ kind: "error", message: status.message, for: key });
        };
        poll();
      };

      runAttempt(0);
    },
    [],
  );

  const isStaleFor = useCallback(
    (filter: string, months: string[]) => {
      if (state.kind === "idle") return false;
      const key = { filter, monthsKey: monthsKeyOf(months) };
      return (
        state.for.filter !== key.filter ||
        state.for.monthsKey !== key.monthsKey
      );
    },
    [state],
  );

  return { state, kick, isStaleFor };
}
